import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import {
  isGroupScoped,
  type ScanGroupOptimized,
  type ScanPartContext,
  type ScanPartOptimized,
  type ScanPhoto,
  type ScanRootOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

export type FocusedGalleryLevel = 'scope' | 'group' | 'task';

export type GalleryScopeTarget = {
  groupId?: string;
  taskId?: string;
};

export type FocusedGallerySection = {
  id: string;
  kind: 'overview' | 'group' | 'task';
  title: string;
  scanPart: ScanPartOptimized;
  scanPartContext: ScanPartContext;
  photos: readonly ScanPhoto[];
};

export type FocusedGalleryGroup = FocusedGallerySection & {
  kind: 'group';
  tasks: readonly FocusedGallerySection[];
  photoCount: number;
};

export type FocusedGalleryModel = {
  level: FocusedGalleryLevel;
  selectedGroupId?: string;
  selectedTaskId?: string;
  overview: FocusedGallerySection;
  groups: readonly FocusedGalleryGroup[];
  isPhotoEmpty: boolean;
  emptyUploadGroups: readonly FocusedGalleryGroup[];
};

const buildTaskSection = (groupId: string, task: ScanTaskOptimized): FocusedGallerySection => ({
  id: task.id,
  kind: 'task',
  title: taskNamePresenter(task),
  scanPart: task,
  scanPartContext: { groupId, taskId: task.id },
  photos: task.photos ?? [],
});

const buildGroupSection = (group: ScanGroupOptimized): FocusedGalleryGroup => {
  const tasks = (group.tasks ?? []).map(task => buildTaskSection(group.id, task));
  const photos = group.photos ?? [];

  return {
    id: group.id,
    kind: 'group',
    title: groupNamePresenter(group),
    scanPart: group,
    scanPartContext: { groupId: group.id },
    photos,
    tasks,
    photoCount: photos.length + tasks.reduce((total, task) => total + task.photos.length, 0),
  };
};

export const isGalleryGroupVisible = (group: ScanGroupOptimized): boolean => group.optimized.allPhotos.length > 0 || isGroupScoped(group);

const scanHasPhotos = (scanRoot: ScanRootOptimized): boolean => (
  !!scanRoot.photos?.length || scanRoot.groups.some(group => (
    !!group.photos?.length || group.tasks?.some(task => !!task.photos?.length)
  ))
);

export const buildFocusedGalleryModel = ({
  scanRoot,
  groups,
  selectedGroupId,
  selectedTaskId,
}: {
  scanRoot: ScanRootOptimized;
  groups: readonly ScanGroupOptimized[];
  selectedGroupId?: string;
  selectedTaskId?: string;
}): FocusedGalleryModel => {
  const selectedGroup = groups.find(group => group.id === selectedGroupId);
  const selectedTask = selectedGroup?.tasks?.find(task => task.id === selectedTaskId);
  const visibleGroups = groups.filter(group => (
    isGalleryGroupVisible(group) || group.id === selectedGroup?.id
  ));

  return {
    level: selectedTask ? 'task' : selectedGroup ? 'group' : 'scope',
    selectedGroupId: selectedGroup?.id,
    selectedTaskId: selectedTask?.id,
    overview: {
      id: scanRoot.id,
      kind: 'overview',
      title: 'Curbside Photo',
      scanPart: scanRoot,
      scanPartContext: {},
      photos: scanRoot.photos ?? [],
    },
    groups: visibleGroups.map(buildGroupSection),
    isPhotoEmpty: !scanHasPhotos(scanRoot),
    emptyUploadGroups: scanRoot.groups.map(buildGroupSection),
  };
};
